'use strict';

import React, {Component, useState} from 'react';
import {observer} from "mobx-react";
import PropTypes from 'prop-types';
import moment from 'moment';

import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Card from 'react-bootstrap/Card';
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import termStore from '@uw-it-sis/lib-react/lib/TermStore';
import SimpleState from '@uw-it-sis/lib-react/lib/SimpleState';
import {isEmpty} from '@uw-it-sis/lib-react/lib/Utils';
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils';
import registrationDateStore from '@uw-it-sis/lib-react-myplan/lib/RegistrationDateStore';

import {NextAction} from "../lib/Constants";
import Acknowledgements from "../prereg/Acknowledgements";
import Address from "../prereg/Address";
import Register from "./Register";
import {addLinkTargetsToHtmlString} from "../common/Utils";

const analyticsStore = appStore.getAnalyticsStore();

const Messaging = ({messages, variant = "info", pretext = "The following issue(s) prevent you from registering:", heading = "You Are Not Able To Register"}) => {
    if (messages.length === 0) {
        return;
    }

    const messagesList = messages.map((msg, index) => {
        return (
            <li key={index}>
                {addLinkTargetsToHtmlString(msg.messageText)}
            </li>
        )
    });

    return (
        <Card className={`alert alert-${variant}`}>
            <Card.Title as="h3" className="alert-heading h4">
                {heading}
            </Card.Title>
            <p>{pretext}</p>
            <ul>
                {messagesList}
            </ul>
        </Card>
    );
}

Messaging.propTypes = {
    messages: PropTypes.array,
    variant: PropTypes.string,
    heading: PropTypes.string,
    pretext: PropTypes.string
}

const AcknowledgementsState = ({onSuccess, isAfterRegDate = false, lastAddDate, regDate}) => {
    let header, body;
    if (regDate && lastAddDate && typeof isAfterRegDate === "boolean") {
        if (isAfterRegDate) {
            header = "Complete Your Pre-Registration Steps In Order To Register";
            body =
                <div>
                    <p>You must complete your required pre-registration steps before you can register.</p>
                    <p>You will be able to register online until {lastAddDate}.</p>
                </div>
        } else {
            header = "Complete Your Required Pre-Registration Acknowledgements Before Registration Begins";
            body =
                <div>
                    <p>Your required pre-registration acknowledgements are now available.</p>
                    <p>You can complete these steps now so that you don't have to do them on your registration date.</p>
                    <p>Your estimated registration date will be {regDate}.</p>
                </div>
        }
    } else {
        header = "Complete Your Pre-Registration Steps In Order To Register";
        body =
            <div>
                <p>You must complete your required pre-registration steps before you can register.</p>
            </div>
    }

    return (
        <Card className="alert alert-warning">
            <Card.Title as="h3" className="alert-heading h4">{header}</Card.Title>
            {body}
            <hr aria-hidden={true} />
            <div className="d-flex justify-content-end">
                <Acknowledgements onSuccess={onSuccess} isAfterRegDate={isAfterRegDate} />
            </div>
        </Card>
    );
}

AcknowledgementsState.propTypes = {
    onSuccess: PropTypes.func.isRequired,
    regDate: PropTypes.string,
    lastAddDate: PropTypes.string,
    isAfterRegDate: PropTypes.bool
}

const AddressModal = ({onSuccess, isAfterRegDate = false, lastAddDate, regDate}) => {
    const [show, setShow] = useState(false);
    const [complete, setComplete] = useState(false);
    const handleClose = () => {
        setShow(false);
        onSuccess();
    }
    const handleShow = () => {
        setShow(true);
        analyticsStore.addEvent("RegPrev-AddressModalOpen", {}, {});
    }

    const handleGoto = (action) => {
        analyticsStore.addEvent("RegPrev-AddressButtonClick", {}, {action: action});
        setComplete(true);
    }

    let modalTitle, modalBody, modalFooter;

    if (complete) {
        modalTitle = "Complete Your Insurance/Optional Charges Step In The New Tab";
        modalBody =
            <>
                <p>After you complete your Insurance/Optional Charges step in the Registration system, close this window to refresh MyPlan and continue preparing for registration.</p>
            </>
        modalFooter =
            <Button variant="primary"
                    onClick={handleClose}>
                Close and Refresh
            </Button>
    } else {
        modalTitle = "Review Your Address Information";
        modalBody = <Address />
        modalFooter =
            <Button variant="primary"
                    href="https://sdb.admin.uw.edu/sisStudents/uwnetid/op_charges.aspx"
                    onClick={() => handleGoto("Insurance/Optional Charges")}
                    target="_blank">
                Continue To The Insurance/Optional Charges Page
            </Button>
    }

    let alertBody, alertHeaderSuffix;
    if (regDate && lastAddDate && typeof isAfterRegDate === "boolean") {
        if (isAfterRegDate) {
            alertHeaderSuffix = "In Order To Register";
            alertBody =
                <div>
                    <p>You must complete your required Address Review and Insurance/Optional Charges steps before you can register.</p>
                    <p>You will be able to register online until {lastAddDate}</p>
                </div>
        } else {
            alertHeaderSuffix = "Before Registration Begins";
            alertBody =
                <div>
                    <p>Your required Address Review and Insurance/Optional Charges steps are now available.</p>
                    <p>You can complete these steps now so that you don’t have to do them on your registration date.</p>
                    <p>Your estimated registration date will be {regDate}.</p>
                </div>
        }
    } else {
        alertBody =
            <div>
                <p>You must complete your required Address Review and Insurance/Optional Charges steps before you can register.</p>
            </div>
    }

    return (
        <>
            <Card className="alert alert-warning">
                <Card.Title as="h3" className="alert-heading h4">Complete Your Address Review and Insurance/Optional Charges Steps {alertHeaderSuffix}</Card.Title>
                {alertBody}
                <hr aria-hidden={true} />
                <div className="d-flex justify-content-end">
                    <Button onClick={handleShow}>Complete Address Review and Insurance/Optional Charges Steps</Button>
                </div>
            </Card>
            <Modal
                show={show}
                size="lg"
                onHide={handleClose}
                backdrop="static"
                aria-labelledby="modal-address-title"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title as="h3" id="modal-address-title">{modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalBody}
                </Modal.Body>
                <Modal.Footer>
                    {modalFooter}
                </Modal.Footer>
            </Modal>
        </>
    );
}

AddressModal.propTypes = {
    onSuccess: PropTypes.func.isRequired,
    regDate: PropTypes.string,
    lastAddDate: PropTypes.string,
    isAfterRegDate: PropTypes.bool
}

class RegistrationSeamless extends Component {
    constructor(props) {
        super(props);
        this.props = props;
    }

    componentDidMount() {
        registrationDateStore.mount();
    }

    componentWillUnmount() {
        registrationDateStore.unmount();
    }

    handleAnalyticsAdd = (eventKey) => {
        analyticsStore.addEvent(eventKey, {}, {});
    }

    buildDoneComponent = () => {
        let component;
        let now = moment(appStore.getNow());
        let dateFormat = "dddd, MMM D";

        // Get the term data for the selected term id (via component prop)
        let selectedTerm = termStore.get('publishedTerms').find(function (term) {
            return term.id === this.props.termId;
        }.bind(this));

        let registrationDateMap = undefined;
        let formattedRegServicesStartDate;

        if (selectedTerm) {
            registrationDateMap = registrationDateStore.getRegistrationDataForTerm(selectedTerm.codeName);
            let regServicesStartDate = TermUtils.findAcademicCalendarDate(selectedTerm, "RegistrationServicesStart");
            formattedRegServicesStartDate = regServicesStartDate?.format(dateFormat) ?? "";
        }

        let formattedRegistrationDate = '';
        let registrationDate, isAfterRegDate;

        if (!isEmpty(registrationDateMap)) {
            registrationDate = registrationDateMap.date;
            formattedRegistrationDate = registrationDate.format(dateFormat);
            isAfterRegDate = now.isAfter(registrationDate);
        }

        let lastAddDate = undefined;
        let formattedLastAddDate;

        if (selectedTerm && selectedTerm.hasOwnProperty("academicCalendar")) {
            selectedTerm.academicCalendar.forEach( function(entry) {
                if (entry.key === 'last_add_day') {
                    // use a time of 11:59pm to check that today's date is past the complete day of the last add day
                    lastAddDate = moment(entry.date + " 23:59", "YYYY-MM-DD hh:mm");
                    formattedLastAddDate = lastAddDate.format(dateFormat);
                }
            });
        }

        let isPastSelectedTermLastAddDate = false;
        if (!isEmpty(lastAddDate)) {
            isPastSelectedTermLastAddDate = now.isAfter(lastAddDate);
        }

        switch (this.props.data.nextAction) {
            case NextAction.REG_SVCS_NOT_OPEN:
                component =
                    <Card className="alert alert-light-gray">
                        <Card.Title as="h3" className="alert-heading h4">
                            Your Pre-Registration Steps Are Not Yet Available
                        </Card.Title>
                        <div>
                            <p>You will be able to complete your required pre-registration steps starting on {formattedRegServicesStartDate}.</p>
                            <p>Your estimated registration date is {formattedRegistrationDate}.</p>
                        </div>
                    </Card>
                break;
            case NextAction.BLOCK:
                component = <Messaging {...this.props.data} variant="danger" heading="You Are Not Able To Register" pretext="The following issue(s) prevent you from registering:" />
                break;
            case NextAction.ACKNOWLEDGE:
                component = <AcknowledgementsState onSuccess={this.props.onSuccess} regDate={formattedRegistrationDate} lastAddDate={formattedLastAddDate} isAfterRegDate={isAfterRegDate} />
                break;
            case NextAction.ADDRESS:
                component = <AddressModal onSuccess={this.props.onSuccess}  regDate={formattedRegistrationDate} lastAddDate={formattedLastAddDate} isAfterRegDate={isAfterRegDate} />
                break;
            case NextAction.STUDENT_REG_NOT_OPEN:
                component =
                    <Card className="alert alert-light-gray">
                        <Card.Title as="h3" className="alert-heading h4">
                            Registration Is Not Yet Open
                        </Card.Title>
                        <div>
                            <p>Your estimated registration date is {formattedRegistrationDate}.</p>
                            <p>You will be able to register online until {formattedLastAddDate}.</p>
                        </div>
                    </Card>
                break;
            case NextAction.REGISTER:
                if ( isPastSelectedTermLastAddDate ) {
                    component =
                        <Card className="alert alert-warning">
                            <Card.Title as="h3" className="alert-heading h4">
                                Registration No Longer Available
                            </Card.Title>
                            <div>
                                <p>The last day to register for {selectedTerm.name} was {formattedLastAddDate}.</p>
                                <p>Please contact your campus registration office for help regarding your current schedule.</p>
                                <ul>
                                    <li><a href="https://www.uwb.edu/registration" target="_blank"
                                           onClick={() => this.handleAnalyticsAdd("RegPrev-RegistrarBothellLink")}>UW Bothell</a></li>
                                    <li><a href="https://registrar.washington.edu" target="_blank"
                                           onClick={() => this.handleAnalyticsAdd("RegPrev-RegistrarSeattleLink")}>UW Seattle</a></li>
                                    <li><a href="http://www.tacoma.uw.edu/node/38232" target="_blank"
                                           onClick={() => this.handleAnalyticsAdd("RegPrev-RegistrarTacomaLink")}>UW Tacoma</a></li>
                                </ul>
                            </div>
                        </Card>
                } else {
                    component =
                        <Card className="alert alert-purple">
                            <Card.Title as="h3" className="alert-heading h4">
                                Registration Is Open
                            </Card.Title>
                            <p>You will be able to register online until {formattedLastAddDate}.</p>
                            <hr aria-hidden={true} />
                            <div className="d-flex justify-content-end">
                                <Register {...this.props} />
                            </div>
                        </Card>
                }
                break;
        }

        return (
            <Row>
                <Col xs={12} sm={7} role="region" aria-label="Your Registration Status">{component}</Col>
                <Col xs={12} sm={5}>
                    <Card>
                        <Card.Body>
                            <Card.Body className="small">
                                <p className="mb-0"><a href="https://sdb.admin.uw.edu/students/uwnetid/register.asp"
                                                       onClick={() => this.handleAnalyticsAdd("RegPrev-SpsLinkClick")}
                                                       target="_blank" className="text-decoration-underline">Go to the Registration system</a> if you need to:</p>
                                <ul>
                                    <li>Resolve registration errors</li>
                                    <li>Add sections by SLN directly</li>
                                    <li>Add sections with variable credits</li>
                                    <li>Drop sections</li>
                                    <li>Complete pre-registration steps</li>
                                </ul>
                            </Card.Body>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    }

    render() {
        return <SimpleState state={registrationDateStore.state} doneComponentBuilder={this.buildDoneComponent} name="RegistrationButton"/>
    }
}

RegistrationSeamless.propTypes = {
    data: PropTypes.object.isRequired,
    selectionCount: PropTypes.number.isRequired,
    onSuccess: PropTypes.func.isRequired,
    termId: PropTypes.string.isRequired,
}

export default observer(RegistrationSeamless);